import { Injectable } from '@angular/core';
import { CompanyAccount } from '@app/shared/models/company-account.model';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { CompanyAccountAdapter } from '../authentication/company-account.adapter';
import { ACCOUNT_SELECTED_KEY, COMPANY_URI } from '../constants/storage-keys';
import { LoginUser } from '@app/shared/models/authenticated-user.model';

@Injectable()
export class SelectedAccountStore {
    private user: LoginUser;
    private selectedAccountSubject: BehaviorSubject<CompanyAccount>;

    constructor(
        private companyAccountAdapter: CompanyAccountAdapter,
        private cookieService: CookieService
    ) {
        this.selectedAccountSubject = new BehaviorSubject<CompanyAccount>(undefined);
    }

    get selectedAccount$(): Observable<CompanyAccount> {
        return this.selectedAccountSubject.asObservable();
    }

    get selectedAccount(): CompanyAccount {
        return this.selectedAccountSubject.value;
    }

    initialize(user: LoginUser) {
        let selectedAccount: CompanyAccount;
        const accounts = [user.serviceAccount, ...user.childAccounts].filter(
            (account) => !!account
        );
        const storedAccount = localStorage.getItem(ACCOUNT_SELECTED_KEY);

        if (storedAccount) {
            selectedAccount = this.companyAccountAdapter.adapt({
                ...JSON.parse(storedAccount),
                rawValue: storedAccount,
            });

            if (!accounts.map((account) => account.id).includes(selectedAccount.id)) {
                selectedAccount = accounts[0];
            }
        } else {
            selectedAccount = accounts[0];
        }

        this.user = user;
        this.storeSelectedAccount(selectedAccount);
        this.selectedAccountSubject.next(selectedAccount);
    }

    update(user: LoginUser): void {
        this.user = user;
        const accounts = [this.user.serviceAccount, ...this.user.childAccounts].filter(
            (account) => !!account
        );
        const storedRawAccount = localStorage.getItem(ACCOUNT_SELECTED_KEY);

        let newAccountValue: CompanyAccount;
        if (storedRawAccount) {
            const current = this.companyAccountAdapter.adapt(JSON.parse(storedRawAccount));
            const updated = accounts.find((account) => account.id === current.id);

            if (!updated) {
                newAccountValue = accounts[0];
            } else if (!current.equal(updated)) {
                newAccountValue = updated;
            }
        } else {
            newAccountValue = accounts[0];
        }

        if (newAccountValue) {
            this.selectedAccountSubject.next(newAccountValue);
            this.storeSelectedAccount(newAccountValue);
        }
    }

    set(companyAccount: CompanyAccount): void {
        this.selectedAccountSubject.next(companyAccount);
        this.storeSelectedAccount(companyAccount);
    }

    reset() {
        this.selectedAccountSubject.next(undefined);
    }

    private storeSelectedAccount(selectedAccount: CompanyAccount) {
        localStorage.setItem(ACCOUNT_SELECTED_KEY, selectedAccount.rawValue);
        this.cookieService.set(COMPANY_URI, JSON.stringify(`/companies/${selectedAccount.id}`));
    }
}
